import { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { photoSrcSet, type Photo } from '../data/locations';
import { focusRing } from './SiteHeader';

const PHONE_WIDTH = 560;
const PANORAMA_ASPECT = 2;
const MAX_STRETCH = 1.15;

interface Tile {
  photo: Photo;
  index: number;
  width: number;
}

interface Row {
  tiles: Tile[];
  height: number;
  justified: boolean;
}

const aspectOf = (p: Photo) => p.width / p.height;

function targetRowHeight(width: number): number {
  if (width < PHONE_WIDTH) return 150;
  if (width < 800) return 220;
  if (width < 1200) return 280;
  return 320;
}

function buildRows(photos: Photo[], width: number, gap: number, singleColumn: boolean): Row[] {
  const fullWidth = (photo: Photo, index: number): Row => ({
    tiles: [{ photo, index, width }],
    height: width / aspectOf(photo),
    justified: true,
  });

  if (singleColumn) return photos.map(fullWidth);

  const target = targetRowHeight(width);
  const rows: Row[] = [];
  let pending: { photo: Photo; index: number }[] = [];

  const fitHeight = (items: { photo: Photo }[]) =>
    (width - gap * (items.length - 1)) / items.reduce((sum, { photo }) => sum + aspectOf(photo), 0);

  const place = (items: { photo: Photo; index: number }[], height: number, justified: boolean): Row => ({
    tiles: items.map(({ photo, index }) => ({ photo, index, width: height * aspectOf(photo) })),
    height,
    justified,
  });

  const settle = () => {
    if (!pending.length) return;
    const height = fitHeight(pending);
    rows.push(height <= target * MAX_STRETCH ? place(pending, height, true) : place(pending, target, false));
    pending = [];
  };

  photos.forEach((photo, index) => {
    if (aspectOf(photo) > PANORAMA_ASPECT) {
      settle();
      rows.push(fullWidth(photo, index));
      return;
    }
    pending.push({ photo, index });
    const height = fitHeight(pending);
    if (height > target) return;
    if (pending.length > 1) {
      const without = pending.slice(0, -1);
      const heightWithout = fitHeight(without);
      if (Math.abs(Math.log(heightWithout / target)) < Math.abs(Math.log(height / target))) {
        rows.push(place(without, heightWithout, true));
        pending = pending.slice(-1);
        return;
      }
    }
    rows.push(place(pending, height, true));
    pending = [];
  });
  settle();
  return rows;
}

export function PhotoGallery({
  photos,
  placeName,
  hrefFor,
  eager = 0,
  singleColumnOnPhones = false,
}: {
  photos: Photo[];
  placeName: string;
  hrefFor: (photo: Photo) => string;
  eager?: number;
  singleColumnOnPhones?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setWidth(el.clientWidth));
    });
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const phone = width < PHONE_WIDTH;
  const gap = phone ? 6 : 12;
  const rows = width ? buildRows(photos, width, gap, singleColumnOnPhones && phone) : [];

  return (
    <div ref={ref} className="flex flex-col" style={{ gap }}>
      {rows.map((row) => (
        <div key={row.tiles[0].photo.id} className="flex" style={{ gap, height: row.height }}>
          {row.tiles.map(({ photo, index, width: tileWidth }) => (
            <Link
              key={photo.id}
              to={hrefFor(photo)}
              style={row.justified ? { flex: `${tileWidth} ${tileWidth} 0px` } : { width: tileWidth, flex: 'none' }}
              className={`group relative block min-w-0 overflow-hidden rounded-md bg-[#0b0e15] after:pointer-events-none after:absolute after:inset-0 after:rounded-md after:ring-1 after:ring-inset after:ring-[#f2c66d]/0 after:transition-shadow after:duration-200 hover:after:ring-[#f2c66d]/60 ${focusRing} focus-visible:outline-offset-2`}
            >
              <img
                src={photo.url}
                srcSet={photoSrcSet(photo)}
                sizes={`${Math.ceil(tileWidth)}px`}
                alt={`${placeName}, photo ${index + 1} of ${photos.length}`}
                loading={index < eager ? 'eager' : 'lazy'}
                decoding="async"
                width={photo.width}
                height={photo.height}
                className="h-full w-full object-cover transition-[filter] duration-200 group-hover:brightness-110"
              />
            </Link>
          ))}
        </div>
      ))}
    </div>
  );
}
